import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { useContactCustomFields } from '../../../../../../../queries';
import { TextInput } from '../../../../../../components/TextInput';
import { styleSheet as editContactStyles } from '../../../../../../components/contacts/EditContactInfo/styles';
import { EntityInfoFieldLabel } from '../../../../../../components/entities/EntityInfoFieldLabel';
import { EntityInfoSection } from '../../../../../../components/entities/EntityInfoSection';
import { styleSheet } from '../styles';

type CustomFieldWithValue = Api.IFormField<string> & {
	value: string;
};

type Props = {
	contact: Api.IContact;
	onCustomFieldsChange: (customFields: CustomFieldWithValue[]) => void;
};

const getInitialValue = (customField: Api.IFormField<string>, contact: Api.IContact) => {
	// Get the value from the contact's custom properties if available
	if (contact.customProperties && contact.customProperties[customField.name]) {
		return contact.customProperties[customField.name];
	}
	return '';
};

export const EditableCustomFields = ({ contact, onCustomFieldsChange }: Props) => {
	const userSession = useUserSession();

	const hasCustomContactFields = userSession.account.additionalInfo?.hasCustomContactFields;

	const customFieldsQuery = useContactCustomFields({
		refetchOnWindowFocus: false,
		enabled: hasCustomContactFields,
	});

	const availableCustomFields = customFieldsQuery.data?.fields;

	// Initialize custom fields with values from contact
	const [customFields, setCustomFields] = React.useState<CustomFieldWithValue[]>([]);

	// Initialize custom fields data when available fields or contact changes
	React.useEffect(() => {
		if (availableCustomFields) {
			const initialCustomFields = availableCustomFields.map(field => ({
				...field,
				value: getInitialValue(field, contact),
			}));
			setCustomFields(initialCustomFields);
			onCustomFieldsChange(initialCustomFields);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availableCustomFields, contact]);

	// Also notify parent when internal customFields state changes
	React.useEffect(() => {
		if (customFields.length > 0) {
			onCustomFieldsChange(customFields);
		}
	}, [customFields, onCustomFieldsChange]);

	const handleCustomFieldChange = (index: number, value: string) => {
		const newCustomFields = [...customFields];
		newCustomFields[index] = {
			...newCustomFields[index],
			value,
		};
		setCustomFields(newCustomFields);
	};

	if (!hasCustomContactFields || !availableCustomFields || availableCustomFields.length === 0) {
		return null;
	}

	return (
		<EntityInfoSection title='Custom Fields'>
			<div className={css(editContactStyles.fieldGroup)}>
				{customFields.map((customField, index) => (
					<div key={`custom-field-${customField.name}`}>
						<EntityInfoFieldLabel title={customField.label} />
						<TextInput
							autoComplete='off'
							inputId={`edit-custom-field-${customField.name}`}
							onChange={e => handleCustomFieldChange(index, e.target.value)}
							placeholder={customField.label}
							type='text'
							value={customField.value}
							className={css(styleSheet.editInput)}
						/>
					</div>
				))}
			</div>
		</EntityInfoSection>
	);
};
